import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useLang } from '../../../../hooks/use-lang';
import { useQueryParams } from '../../../../hooks/use-query-params';
import { TaskStatusType, TaskType } from '../../../../store/api/tasks-api';
import { useEmployeesStore } from '../../../../store/use-employee-store';
import { IdValue } from '../../../../types/props';
import { changeStatuses, statusTitleMap, statusVariantMap } from './use-task-list';

export const useTaskListUtils = () => {
  const { t, getLocale } = useLang();
  const { staff } = useEmployeesStore();
  const { getQueryParams, addQueryParams } = useQueryParams();
  const params = getQueryParams();

  const isCommon = useLocation().pathname.split('/')[3] === 'common';

  const getEmployee = useCallback(
    (item: TaskType) => {
      const result = staff?.find((staff) => staff.id === item.received_by.id);

      if (result) {
        return {
          id: result.id.toString(),
          value: `${getLocale(result?.name)} ${getLocale(result?.surname)}`,
        };
      }

      const isDeletedStaff = !!item.received_by.id;
      if (isDeletedStaff) {
        return {
          id: item.received_by.id.toString(),
          value: `*${getLocale(item.received_by.name)}*`,
        };
      }

      return undefined;
    },
    [getLocale, staff],
  );

  const getEmployeeOptions = useCallback(
    (item: TaskType) => {
      return (staff ?? [])
        .filter((staff) => staff.places?.some((place) => place.id === item.place_id))
        .map((item) => ({
          id: item.id.toString(),
          value: `${getLocale(item.name)} ${getLocale(item.surname)}`,
        }));
    },
    [staff, getLocale],
  );

  const getStatusLabel = (status: TaskStatusType, isRequiresExecutor: boolean) => {
    return isRequiresExecutor ? t('Требует назначения ответственного') : t(statusTitleMap[status]);
  };

  const getStatusVariant = (status: TaskStatusType, isRequiresExecutor: boolean) => {
    return isRequiresExecutor ? 'danger' : statusVariantMap[status];
  };

  const isStatusEditable = (status: TaskStatusType) => {
    return changeStatuses.includes(status);
  };

  const isDeletedEmployee = (employee?: IdValue) => {
    return !!(!employee?.value.startsWith('*') && employee?.value.endsWith('*'));
  };

  return {
    getEmployee,
    getEmployeeOptions,
    getStatusLabel,
    getStatusVariant,
    isStatusEditable,
    isDeletedEmployee,
    addQueryParams,
    getLocale,
    isCommon,
    params,
    t,
  };
};
